import { useEffect, useRef } from "react"

import { useQueries } from "@tanstack/react-query"
import * as d3 from "d3"
import { feature } from "topojson-client"

import { getBalanceRegionTrips } from "../api/getBalanceRegionTrips"

import type {
  BalanceMapProps,
  BalanceRegion,
  StateGeometry,
  StateProperties,
  StatesTopology,
} from "../types/balanceMapTypes"
import type { Feature, FeatureCollection, Geometry, Polygon } from "geojson"

const MIDWEST_STATES = new Set([
  "Illinois",
  "Iowa",
  "Michigan",
  "Minnesota",
  "Wisconsin",
  "Indiana",
])

const WIDTH = 800
const HEIGHT = 600

// Fill opacity applied to every region, regardless of color.
const REGION_OPACITY = 0.5
// Fill opacity / stroke width applied to a region while it's hovered in the balance table.
const HIGHLIGHT_OPACITY = 0.85
const REGION_STROKE_WIDTH = 1.5
const HIGHLIGHT_STROKE_WIDTH = 3
// Duration of the ease in/out when a region is hovered/unhovered.
const HIGHLIGHT_TRANSITION_MS = 200
// Scrim shown over the whole map (states + other regions) while a region is hovered.
const OVERLAY_COLOR = "#6b7280"
const OVERLAY_OPACITY = 0.25

// Half-dimensions of the label pill, used to keep it fully on-screen.
const LABEL_HALF_WIDTH = 40
const LABEL_HALF_HEIGHT = 22
// Extra gap kept between a clamped label pill and the SVG edge.
const EDGE_PADDING = 10
// Minimum gap kept between two label pills.
const LABEL_GAP = 8

/**
 * Computes the signed area of a ring.
 * Positive = counter-clockwise, Negative = clockwise.
 */
function signedArea(coords: [number, number][]): number {
  let area = 0
  for (let i = 0, j = coords.length - 1; i < coords.length; j = i++) {
    area += (coords[j][1] + coords[i][1]) * (coords[j][0] - coords[i][0])
  }
  return area / 2
}

/**
 * Ensures the ring is counter-clockwise (GeoJSON outer ring convention).
 */
function ensureCounterClockwise(coords: [number, number][]): [number, number][] {
  return signedArea(coords) < 0 ? [...coords].reverse() : coords
}

interface LabelPosition {
  region: BalanceRegion
  x: number
  y: number
}

function clampToBounds(pos: LabelPosition): void {
  pos.x = Math.min(
    Math.max(pos.x, LABEL_HALF_WIDTH + EDGE_PADDING),
    WIDTH - LABEL_HALF_WIDTH - EDGE_PADDING,
  )
  pos.y = Math.min(
    Math.max(pos.y, LABEL_HALF_HEIGHT + EDGE_PADDING),
    HEIGHT - LABEL_HALF_HEIGHT - EDGE_PADDING,
  )
}

/**
 * Nudges overlapping label pills apart (and re-clamps them on-screen)
 * until none overlap or the iteration budget runs out. Two adjacent, small
 * regions can otherwise end up with label centroids close enough that their
 * fixed-size pills overlap.
 */
function resolveLabelOverlaps(positions: LabelPosition[]): void {
  const minDx = LABEL_HALF_WIDTH * 2 + LABEL_GAP
  const minDy = LABEL_HALF_HEIGHT * 2 + LABEL_GAP
  const MAX_ITERATIONS = 100

  for (let iter = 0; iter < MAX_ITERATIONS; iter++) {
    let moved = false

    for (let i = 0; i < positions.length; i++) {
      for (let j = i + 1; j < positions.length; j++) {
        const a = positions[i]
        const b = positions[j]
        const dx = b.x - a.x
        const dy = b.y - a.y
        const overlapX = minDx - Math.abs(dx)
        const overlapY = minDy - Math.abs(dy)

        if (overlapX <= 0 || overlapY <= 0) continue

        moved = true
        // Separate along whichever axis needs the smaller push.
        if (overlapX < overlapY) {
          const shift = overlapX / 2 + 0.5
          const sign = dx >= 0 ? 1 : -1
          a.x -= shift * sign
          b.x += shift * sign
        } else {
          const shift = overlapY / 2 + 0.5
          const sign = dy >= 0 ? 1 : -1
          a.y -= shift * sign
          b.y += shift * sign
        }

        clampToBounds(a)
        clampToBounds(b)
      }
    }

    if (!moved) break
  }
}

interface RegionTripCounts {
  in: number
  out: number
}

export default function BalanceMap({ regions = [], hoveredRegionId = null }: BalanceMapProps) {
  const svgRef = useRef<SVGSVGElement>(null)
  const hoveredRegionIdRef = useRef<number | null>(hoveredRegionId)
  // Holds the latest known trip counts so the topology-draw effect below
  // (which only depends on `regions`, not on trip data) can read a
  // current value no matter which effect finishes first.
  const tripCountsRef = useRef<Map<number, RegionTripCounts>>(new Map())
  hoveredRegionIdRef.current = hoveredRegionId

  const tripsQueries = useQueries({
    queries: regions.map((region) => ({
      queryKey: ["balanceRegionsTrips", { id: region.id }],
      queryFn: () => getBalanceRegionTrips(region.id),
      refetchInterval: 15000,
    })),
  })

  // Keeps tripCountsRef current and patches any already-drawn label pills
  // in place whenever a region's trip counts change, since the pills are
  // painted imperatively by d3 and won't re-render on their own when this
  // query data updates.
  useEffect(() => {
    const counts = new Map<number, RegionTripCounts>()
    regions.forEach((region, i) => {
      const trips = tripsQueries[i]?.data
      counts.set(region.id, {
        in: trips?.today.in.length ?? 0,
        out: trips?.today.out.length ?? 0,
      })
    })
    tripCountsRef.current = counts

    const svgNode = svgRef.current
    if (!svgNode) return

    const svg = d3.select(svgNode)
    counts.forEach((count, regionId) => {
      const labelGroup = svg.select<SVGGElement>(`g.region-labels g[data-region-id="${regionId}"]`)
      if (labelGroup.empty()) return
      labelGroup.select('[data-role="in-count"]').text(`In: ${count.in}`)
      labelGroup.select('[data-role="out-count"]').text(`Out: ${count.out}`)
    })
  }, [regions, tripsQueries])

  useEffect(() => {
    // Guards against React StrictMode's dev-only double-invoke: without this,
    // the first invocation's async draw can still land after a second
    // invocation has started, leaving duplicate states/regions/overlay nodes.
    let cancelled = false

    const svg = d3.select(svgRef.current)
    svg.selectAll("*").remove()

    const projection = d3.geoAlbersUsa().scale(6000).translate([-100, 900])

    const pathGenerator = d3.geoPath().projection(projection)

    fetch("https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json")
      .then((res) => res.json())
      .then((topology: StatesTopology) => {
        if (cancelled) return

        const initialHoveredRegionId = hoveredRegionIdRef.current
        const states = feature(topology, topology.objects.states) as FeatureCollection<
          Geometry,
          StateProperties
        >

        const nameMap = new Map<string, string>()
        ;(topology.objects.states.geometries as StateGeometry[]).forEach((g) => {
          nameMap.set(g.id, g.properties.name)
        })

        svg
          .append("g")
          .selectAll("path")
          .data(
            states.features.filter((d: Feature<Geometry, StateProperties>) => {
              const name = nameMap.get(d.id as string)
              return name && MIDWEST_STATES.has(name)
            }),
          )
          .join("path")
          .attr("d", (d: Feature<Geometry, StateProperties>) => pathGenerator(d))
          .attr("fill", "rgb(200, 200, 200)")
          .attr("stroke", "#fff")
          .attr("stroke-width", 0.8)
          .append("title")
          .text((d: Feature<Geometry, StateProperties>) => nameMap.get(d.id as string) ?? "")

        // Draw regions. Paths and labels live in separate sub-groups (labels
        // always appended after, so always on top) so that reparenting a
        // single region back out of the way on unhover can't push its path
        // above another region's label pill.
        const regionGroup = svg.append("g").attr("class", "regions")
        const pathsGroup = regionGroup.append("g").attr("class", "region-paths")
        const labelsGroup = regionGroup.append("g").attr("class", "region-labels")
        const labelPositions: LabelPosition[] = []

        regions.forEach((region) => {
          let closedCoords: [number, number][] =
            region.coordinates[0] !== region.coordinates[region.coordinates.length - 1]
              ? [...region.coordinates, region.coordinates[0]]
              : [...region.coordinates]

          closedCoords = ensureCounterClockwise(closedCoords)

          // Region coordinates are stored as [latitude, longitude], but
          // GeoJSON (and d3's projections) require [longitude, latitude].
          const lngLatCoords: [number, number][] = closedCoords.map(([lat, lng]) => [lng, lat])

          const geoFeature: Feature<Polygon> = {
            type: "Feature",
            properties: {},
            geometry: {
              type: "Polygon",
              coordinates: [lngLatCoords],
            },
          }

          pathsGroup
            .append("path")
            .datum(geoFeature)
            .attr("d", pathGenerator)
            .attr("data-region-id", region.id)
            .attr("fill", region.color ?? "#4a90d9")
            .attr(
              "fill-opacity",
              region.id === initialHoveredRegionId ? HIGHLIGHT_OPACITY : REGION_OPACITY,
            )
            .attr("stroke", region.color ?? "#4a90d9")
            .attr(
              "stroke-width",
              region.id === initialHoveredRegionId ? HIGHLIGHT_STROKE_WIDTH : REGION_STROKE_WIDTH,
            )
            .append("title")
            .text(region.name ?? String(region.id))

          // Compute centroid for label placement, then clamp it so the label
          // pill stays fully within the SVG viewport even if the region's
          // centroid falls near or past an edge.
          const centroid = pathGenerator.centroid(geoFeature)
          if (!centroid || isNaN(centroid[0]) || isNaN(centroid[1])) return

          const position: LabelPosition = { region, x: centroid[0], y: centroid[1] }
          clampToBounds(position)
          labelPositions.push(position)
        })

        // Push apart any label pills that ended up overlapping (e.g. two
        // small, adjacent regions with nearby centroids) before drawing them.
        resolveLabelOverlaps(labelPositions)

        labelPositions.forEach(({ region, x, y }) => {
          const counts = tripCountsRef.current.get(region.id)
          const inCount: number | string = counts?.in ?? 0
          const outCount: number | string = counts?.out ?? 0

          const labelGroup = labelsGroup
            .append("g")
            .attr("data-region-id", region.id)
            .attr("transform", `translate(${x}, ${y})`)

          // Background pill
          labelGroup
            .append("rect")
            .attr("x", -40)
            .attr("y", -22)
            .attr("width", 80)
            .attr("height", 50)
            .attr("rx", 6)
            .attr("fill", "white")
            .attr("fill-opacity", 0.85)
            .attr("stroke", region.color ?? "#4a90d9")
            .attr("stroke-width", 1)

          // "In" value
          labelGroup
            .append("text")
            .attr("data-role", "in-count")
            .attr("x", 0)
            .attr("y", -2)
            .attr("text-anchor", "middle")
            .attr("font-size", "14px")
            .attr("fill", "var(--foreground)")
            .text(`In: ${inCount}`)

          // "Out" value
          labelGroup
            .append("text")
            .attr("data-role", "out-count")
            .attr("x", 0)
            .attr("y", 16)
            .attr("text-anchor", "middle")
            .attr("font-size", "14px")
            .attr("fill", "var(--foreground)")
            .text(`Out: ${outCount}`)
        })

        // Scrim painted above everything; the hovered region is reparented in
        // front of it (see the effect below) so it reads as a cutout.
        svg
          .append("rect")
          .attr("class", "hover-overlay")
          .attr("width", WIDTH)
          .attr("height", HEIGHT)
          .attr("fill", OVERLAY_COLOR)
          .attr("fill-opacity", 0)
          .attr("pointer-events", "none")
      })

    return () => {
      cancelled = true
    }
  }, [regions])

  // Re-styles the affected region path directly, rather than redrawing the whole
  // map, so hovering over the table doesn't retrigger the topology fetch.
  useEffect(() => {
    const svgNode = svgRef.current
    if (!svgNode) return

    const pathsGroupNode = svgNode.querySelector<SVGGElement>("g.region-paths")
    const labelsGroupNode = svgNode.querySelector<SVGGElement>("g.region-labels")

    // Undo any previous reparenting before (re-)raising the currently hovered
    // region, restoring each element to its own sub-group (rather than back
    // onto the end of a single shared group) so a restored path can never
    // land on top of another region's label pill.
    svgNode.querySelectorAll("[data-region-id]").forEach((el) => {
      if (el.parentNode !== svgNode) return
      if (el instanceof SVGPathElement) pathsGroupNode?.appendChild(el)
      else labelsGroupNode?.appendChild(el)
    })

    d3.select(svgNode)
      .selectAll<SVGPathElement, unknown>("path[data-region-id]")
      .transition()
      .duration(HIGHLIGHT_TRANSITION_MS)
      .ease(d3.easeCubicInOut)
      .attr("fill-opacity", function () {
        const id = Number(this.getAttribute("data-region-id"))
        return id === hoveredRegionId ? HIGHLIGHT_OPACITY : REGION_OPACITY
      })
      .attr("stroke-width", function () {
        const id = Number(this.getAttribute("data-region-id"))
        return id === hoveredRegionId ? HIGHLIGHT_STROKE_WIDTH : REGION_STROKE_WIDTH
      })

    d3.select(svgNode)
      .selectAll<SVGRectElement, unknown>("rect.hover-overlay")
      .transition()
      .duration(HIGHLIGHT_TRANSITION_MS)
      .ease(d3.easeCubicInOut)
      .attr("fill-opacity", hoveredRegionId === null ? 0 : OVERLAY_OPACITY)

    if (hoveredRegionId !== null) {
      svgNode
        .querySelectorAll(
          `path[data-region-id="${hoveredRegionId}"], g[data-region-id="${hoveredRegionId}"]`,
        )
        .forEach((el) => svgNode.appendChild(el))
    }
  }, [hoveredRegionId])

  return (
    <div className="flex rounded-md shadow-sm border">
      <svg ref={svgRef} width={WIDTH} height={HEIGHT} className="rounded-md" />
    </div>
  )
}
